import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProgressSummary } from "../../api/process";
import WidgetCard from "./WidgetCard";

export default function ProgressStatusWidget() {
  const { id: projectId } = useParams();
  const [baselineProgress, setBaselineProgress] = useState(0);
  const [actualProgress, setActualProgress] = useState(0);
  const [earned, setEarned] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const progress = await getProgressSummary();
        setBaselineProgress(
          progress.project.summary.baseline_progress_rate || 0
        );
        setActualProgress(progress.project.summary.actual_progress_rate || 0);
        setEarned(progress.project.summary.earned_value_rate || 0);
      } catch (error) {
        console.error("공정 데이터 불러오기 실패:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [projectId]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 w-full text-center text-slate-500">
        데이터를 불러오는 중입니다...
      </div>
    );
  }

  // 진행률 차이 계산 (실제 - 계획)
  const progressDiff = actualProgress - baselineProgress;
  const isAhead = progressDiff > 0;
  const isBehind = progressDiff < 0;

  return (
    <WidgetCard>
      <div className="space-y-3.5">
        {/* 🔵 실제 진행률 */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">실제 진행률</span>
            <span className="text-base font-bold text-blue-700">{actualProgress.toFixed(1)}%</span>
          </div>

          {/* 실제 진행률 바 */}
          <div className="relative h-8 bg-slate-100 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-700 ease-out flex items-center justify-end pr-2.5"
              style={{ width: `${actualProgress}%` }}
            >
              {actualProgress > 12 && (
                <span className="text-[11px] font-bold text-white drop-shadow">
                  {actualProgress.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 🟢 Baseline 진행률 */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-600">계획 진행률 (Baseline)</span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-green-600">{baselineProgress.toFixed(1)}%</span>
              {/* 차이 표시 */}
              {Math.abs(progressDiff) > 0.5 && (
                <span className={`text-[9px] font-medium ${isAhead ? 'text-blue-600' : isBehind ? 'text-amber-600' : 'text-slate-400'}`}>
                  ({isAhead ? '+' : ''}{progressDiff.toFixed(1)}%p)
                </span>
              )}
            </div>
          </div>
          <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${baselineProgress}%` }}
            />
          </div>
        </div>

        {/* 구분선 */}
        <div className="border-t border-slate-200 pt-3.5" />

        {/* 🟡 기성률 */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">기성률</span>
            <span className="text-base font-bold text-amber-600">{earned.toFixed(1)}%</span>
          </div>
          <div className="relative h-8 bg-slate-100 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-700 ease-out flex items-center justify-end pr-2.5"
              style={{ width: `${earned}%` }}
            >
              {earned > 12 && (
                <span className="text-[11px] font-bold text-white drop-shadow">
                  {earned.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 간단 설명 */}
        <div className="pt-2 space-y-1 bg-slate-50 rounded-lg p-2.5 -mx-1">
          <div className="flex items-start gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1 flex-shrink-0" />
            <p className="text-[9px] text-slate-600 leading-relaxed">
              <span className="font-semibold">실제 진행률:</span> 현재 시점 기준 실제 완료된 작업 비율
            </p>
          </div>
          <div className="flex items-start gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1 flex-shrink-0" />
            <p className="text-[9px] text-slate-600 leading-relaxed">
              <span className="font-semibold">계획 진행률:</span> Baseline 일정 기준 계획된 진척도
            </p>
          </div>
          <div className="flex items-start gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1 flex-shrink-0" />
            <p className="text-[9px] text-slate-600 leading-relaxed">
              <span className="font-semibold">기성률:</span> 발주처 승인된 기성 완료 비율
            </p>
          </div>
        </div>
      </div>
    </WidgetCard>
  );
}
